import { ApolloProvider } from '@apollo/client'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import { apolloPlayer } from './apollo'
import { getActivePlayerKey, setPlayerKey } from './auth-state'

/**
 * Claims the `:playerKey` route param as THIS TAB's identity, so the Apollo
 * nano-id link can read it on every request without prop-drilling, AND wraps
 * the child routes in the player-key Apollo provider.
 *
 * The claim happens SYNCHRONOUSLY during render — not in a useEffect —
 * because the first child render fires Apollo queries immediately and the
 * link reads the key at that moment. A useEffect-deferred write would leave
 * the first request without a header. React docs explicitly endorse "syncing
 * with an external store" during render when the write is idempotent.
 *
 * Tab-local, deliberately: the key used to live only in localStorage, which
 * every tab on the origin shares, so opening a second player's link made
 * every already-open tab authenticate as that player — see auth-state.ts.
 *
 * When this tab changes key we ALSO wipe the Apollo cache. The cache is a
 * tab-lifetime singleton; entities from KEY_A's session — including the
 * `currentPlayer` root field — would otherwise flash on screen for the new key
 * until the network settles. A first load in a tab has no previous key and an
 * empty cache, so there is nothing to wipe.
 */
const PlayerKeyLoader = () => {
  const { playerKey } = useParams<{ playerKey: string }>()

  if (!playerKey) return <Navigate to="/no-key" replace />

  const previousKey = getActivePlayerKey()
  if (previousKey !== playerKey) {
    setPlayerKey(playerKey)
    // Only a real key SWITCH can leak — a first load has an empty cache.
    //
    // resetStore, not clearStore (POL-85): clearStore passes
    // discardWatches:true, which empties InMemoryCache.watches, while every
    // QueryInfo keeps its `lastWatch` and so believes it is still watching.
    // updateWatch() re-registers only when the watch options change, so those
    // queries never listen to the cache again — for the rest of the session a
    // mutation's cache write reaches nobody. Because this runs during render
    // and clearStore defers to a microtask, the victim was whichever query had
    // already mounted: the shell's `currentPlayer`, which is exactly the copy
    // the score screen reads `locked` from.
    //
    // resetStore wipes the same data, keeps the watches and refetches under the
    // new key — which is what the comment here used to claim clearStore did.
    if (previousKey) {
      apolloPlayer.resetStore().catch(() => {
        /* rejects if a query is mid-flight; the refetch below covers it. */
      })
    }
  }

  return (
    // Keyed so a switch remounts the subtree and no ObservableQuery from the
    // previous player carries over. A no-op while the key is stable.
    <ApolloProvider client={apolloPlayer} key={playerKey}>
      <Outlet />
    </ApolloProvider>
  )
}

export default PlayerKeyLoader
